import { useSelector } from 'react-redux'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import classnames from 'classnames'

// components
import { Text, Format, If, Unless } from '../../../../..'
import QuestionSingle from '../../../../Questions/QuestionSingleContainer'

// selectors
import {
  selectAnnualAppreciationPercent,
  selectAppreciationGrowthStatus,
  selectAppreciationYearsOfData,
  selectAnnualAppreciationValue,
  selectEquityValue,
  selectAppreciationFiveYearEquity,
  selectAppreciationTenYearEquity,
} from '../../../../../../store/selectors/shouldYouSell'

import { selectLatestQuartileMarketValue } from '../../../../../../store/selectors/marketRecord'

// utils
import { QUESTIONS } from '../../../../../../constants/messages'
import { MARKET_VALUE_BREAK_POINTS } from '../../../../../../constants/marketRecords'

// constants
import {
  SHOULD_YOU_SELL_HIGH_GROWTH_STATUS,
  SHOULD_YOU_SELL_MODERATE_GROWTH_STATUS,
  SHOULD_YOU_SELL_SLOW_GROWTH_STATUS,
  SHOULD_YOU_SELL_RISKY_GROWTH_STATUS,
} from '../../../../../../constants/shouldYouSell'
import { HOME_APPRECIATION_TOPIC_KEY } from '../../../../../../constants/topicKeys'

// styles
import styles from '../Appreciation.module.scss'

const message = defineMessages({
  positive: {
    id: 'ShouldYouSell.question.should.I.hold.on',
    defaultMessage: 'Should I hold on to my house?',
  },
  neutral: {
    id: 'ShouldYouSell.question.what.best.move',
    defaultMessage: "What's my best move: stay or sell?",
  },
})

const statusColor = status => {
  switch (status) {
    case SHOULD_YOU_SELL_HIGH_GROWTH_STATUS:
    case SHOULD_YOU_SELL_MODERATE_GROWTH_STATUS:
      return 'green'
    default:
      return 'orange'
  }
}

const AppreciationBody = () => {
  const intl = useIntl()
  const appreciationPercent = useSelector(selectAnnualAppreciationPercent)
  const growthStatus = useSelector(selectAppreciationGrowthStatus)
  const yearsOfData = useSelector(selectAppreciationYearsOfData)
  const annualAppreciationValue = useSelector(selectAnnualAppreciationValue)
  const equityValue = useSelector(selectEquityValue)
  const fiveYearsNetWorth = useSelector(selectAppreciationFiveYearEquity)
  const tenYearsNetWorth = useSelector(selectAppreciationTenYearEquity)
  const positiveGrowthStatus =
    growthStatus === SHOULD_YOU_SELL_HIGH_GROWTH_STATUS ||
    growthStatus === SHOULD_YOU_SELL_MODERATE_GROWTH_STATUS

  const textColor = statusColor(growthStatus)

  const latestMarketValue = useSelector(selectLatestQuartileMarketValue)

  const SubHeaderMessage = () => {
    let marketMessage

    if (latestMarketValue > MARKET_VALUE_BREAK_POINTS.HIGH) {
      marketMessage = (
        <FormattedMessage
          id='ShouldYouSell.appreciation.subheader.market.high'
          defaultMessage='you may want to hang tight since your {market}'
          values={{
            market: (
              <span className={styles.bold}>
                <FormattedMessage
                  id='ShouldYouSell.appreciation.market.high'
                  defaultMessage='market is in demand'
                />
              </span>
            ),
          }}
        />
      )
    } else if (latestMarketValue > MARKET_VALUE_BREAK_POINTS.MODERATE) {
      marketMessage = (
        <FormattedMessage
          id='ShouldYouSell.appreciation.subheader.market.balanced'
          defaultMessage='you may want to wait it out since your {market}'
          values={{
            market: (
              <span className={styles.bold}>
                <FormattedMessage
                  id='ShouldYouSell.appreciation.market.balanced'
                  defaultMessage='market is balanced'
                />
              </span>
            ),
          }}
        />
      )
    } else {
      marketMessage = (
        <FormattedMessage
          id='ShouldYouSell.appreciation.subheader.market.low'
          defaultMessage='you may want to sell since your {market}'
          values={{
            market: (
              <span className={styles.bold}>
                <FormattedMessage
                  id='ShouldYouSell.appreciation.market.low'
                  defaultMessage='market is cooling'
                />
              </span>
            ),
          }}
        />
      )
    }
    return marketMessage
  }

  return (
    <div>
      <Text variant='neutralCool' variantCount='900'>
        <FormattedMessage
          id='ShouldYouSell.appreciation.header'
          defaultMessage='Over the last {year} years, your home value has {direction} an average of {percent}'
          values={{
            year: Math.round(yearsOfData),
            direction:
              appreciationPercent > 0 ? (
                <FormattedMessage
                  id='ShouldYouSell.appreciation.year.grown'
                  defaultMessage='grown'
                />
              ) : (
                <FormattedMessage
                  id='ShouldYouSell.appreciation.year.declined'
                  defaultMessage='declined'
                />
              ),
            percent: (
              <span
                className={classnames(
                  styles.percent,
                  styles[`color-${textColor}`]
                )}
              >
                <Format.Percentage
                  value={appreciationPercent}
                  format='0.0'
                  roundLessThanOne={false}
                />
                <span className={styles.year}>
                  <FormattedMessage
                    id='ShouldYouSell.appreciation.year.body'
                    defaultMessage=' / YEAR'
                  />
                </span>
              </span>
            ),
          }}
        />
      </Text>
      <If condition={positiveGrowthStatus}>
        <Text
          variant='neutralCool'
          variantCount='600'
          className={styles.annualAmount}
        >
          <FormattedMessage
            id='ShouldYouSell.appreciation.subheader'
            defaultMessage='which equals around {appreciationValue} annually!'
            values={{
              appreciationValue: (
                <span className={styles.bold}>
                  <Format.Currency value={annualAppreciationValue} />
                </span>
              ),
            }}
          />
        </Text>
      </If>
      <If condition={growthStatus === SHOULD_YOU_SELL_SLOW_GROWTH_STATUS}>
        <Text
          variant='neutralCool'
          variantCount='600'
          className={styles.annualAmount}
        >
          <FormattedMessage
            id='ShouldYouSell.appreciation.subheader.slow'
            defaultMessage='which equals around {appreciationValue} annually - {market}'
            values={{
              appreciationValue: (
                <span className={styles.bold}>
                  <Format.Currency value={annualAppreciationValue} />
                </span>
              ),
              market: <SubHeaderMessage />,
            }}
          />
        </Text>
      </If>
      <If condition={growthStatus === SHOULD_YOU_SELL_RISKY_GROWTH_STATUS}>
        <Text
          variant='neutralCool'
          variantCount='600'
          className={styles.annualAmount}
        >
          <FormattedMessage
            id='ShouldYouSell.appreciation.subheader.risky'
            defaultMessage='which equals around {appreciationValue} annually - {market}'
            values={{
              appreciationValue: (
                <span className={styles.risky}>
                  <Format.Currency value={annualAppreciationValue} />
                </span>
              ),
              market: <SubHeaderMessage />,
            }}
          />
        </Text>
      </If>
      <Unless condition={growthStatus === SHOULD_YOU_SELL_RISKY_GROWTH_STATUS}>
        <Text variant='neutralCool' variantCount='900'>
          <FormattedMessage
            id='ShouldYouSell.appreciation.netWorth'
            defaultMessage='If you stayed, what could your {netWorth} net worth potentially be?'
            values={{
              netWorth: (
                <span className={styles.bold}>
                  <Format.Currency value={equityValue} abbreviate />
                </span>
              ),
            }}
          />
        </Text>
        <div className={styles.netWorth}>
          <div>
            <Text size='xl' variant='primary'>
              <Format.Currency value={fiveYearsNetWorth} abbreviate />
            </Text>
            <Text variant='neutralCool' variantCount='600'>
              <FormattedMessage
                id='ShouldYouSell.appreciation.five.years'
                defaultMessage='in 5 years'
              />
            </Text>
          </div>
          <hr className={styles.hr} />
          <div>
            <Text size='xl' variant='primary'>
              <Format.Currency value={tenYearsNetWorth} abbreviate />
            </Text>
            <Text variant='neutralCool' variantCount='600'>
              <FormattedMessage
                id='ShouldYouSell.appreciation.ten.years'
                defaultMessage='in 10 years'
              />
            </Text>
          </div>
        </div>
        <Text
          variant='neutralCool'
          variantCount='600'
          className={styles.averageAppreciation}
        >
          <FormattedMessage
            id='ShouldYouSell.appreciation.average.growth'
            defaultMessage='assuming an average growth of {appreciationPercent} each year and that you pay your loan each month'
            values={{
              appreciationPercent: (
                <Format.Percentage
                  value={appreciationPercent}
                  format='0.0'
                  roundLessThanOne={false}
                />
              ),
            }}
          />
        </Text>
      </Unless>
      <div className={styles.question}>
        <QuestionSingle
          questionTopic={intl.formatMessage(QUESTIONS.homeAppreciationTopic)}
          questionTopicKey={HOME_APPRECIATION_TOPIC_KEY}
          placeholder={
            positiveGrowthStatus
              ? intl.formatMessage(message.positive)
              : intl.formatMessage(message.neutral)
          }
        />
      </div>
    </div>
  )
}

export default AppreciationBody
